// Decides what to show: loading, sign-in, a message, or the app proper. The router (HashRouter in main.tsx, a
// MemoryRouter in tests) is supplied from outside.

import type { ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router";
import { DialogsProvider } from "./dialogs/DialogsProvider";
import { isApprover } from "./lib/leave";
import { Admin } from "./pages/Admin";
import { Approvals } from "./pages/Approvals";
import { Calendar } from "./pages/Calendar";
import { Dashboard } from "./pages/Dashboard";
import { Planner } from "./pages/Planner";
import { OrgChartPage } from "./pages/OrgChart";
import { Login, MessagePage, Splash } from "./shell/Login";
import { Shell } from "./shell/Shell";
import type { AppStore } from "./store";
import { StoreProvider, useStore, useStoreState } from "./store/StoreProvider";
import { useCtx } from "./store/useCtx";
import { ToastProvider } from "./ui/Toasts";

interface EntryActions {
  onEnterDemo?: () => void;
  onExitDemo?: () => void;
}

export function App({ store, onEnterDemo, onExitDemo }: { store: AppStore } & EntryActions) {
  return (
    <StoreProvider store={store}>
      <ToastProvider>
        <AuthGate onEnterDemo={onEnterDemo} onExitDemo={onExitDemo} />
      </ToastProvider>
    </StoreProvider>
  );
}

function AuthGate({ onEnterDemo, onExitDemo }: EntryActions) {
  const store = useStore();
  const { authState, error } = useStoreState();
  const signOut = () => void store.signOut();

  switch (authState) {
    case "loading":
      return <Splash />;
    case "signed-out":
      return <Login onEnterDemo={onEnterDemo} onExitDemo={onExitDemo} />;
    case "inactive":
      return (
        <MessagePage title="Account deactivated" onSignOut={signOut} onExitDemo={onExitDemo}>
          Your account has been deactivated. Please contact HR if you think this is a mistake.
        </MessagePage>
      );
    case "no-access":
      return (
        <MessagePage title="You don't have access yet" onSignOut={signOut}>
          {
            "You don't have access to Playbrush Holiday yet. Please contact your team lead or HR to get access to the platform."
          }
        </MessagePage>
      );
    case "error":
      if (store.mode === "demo") {
        return (
          <MessagePage title="Demo unavailable" onRetry={() => void store.init()} onExitDemo={onExitDemo}>
            {error || "Please try again in a moment."}
          </MessagePage>
        );
      }
      return (
        <MessagePage title="Something went wrong" onSignOut={signOut}>
          {error || "Please try again in a moment."}
        </MessagePage>
      );
    case "ready":
      return (
        <DialogsProvider>
          <Screens onExitDemo={onExitDemo} />
        </DialogsProvider>
      );
  }
}

function Screens({ onExitDemo }: Pick<EntryActions, "onExitDemo">) {
  return (
    <Routes>
      <Route element={<Shell onExitDemo={onExitDemo} />}>
        <Route index element={<Dashboard />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="year" element={<Planner />} />
        <Route path="org-chart" element={<OrgChartPage />} />
        <Route
          path="approvals"
          element={
            <Only when={(ctx) => isApprover(ctx.me, ctx)}>
              <Approvals />
            </Only>
          }
        />
        <Route
          path="admin/:tab?"
          element={
            <Only when={(ctx) => ctx.me.role === "hr"}>
              <Admin />
            </Only>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Shows its page only to people allowed to see it; everyone else lands on the dashboard. */
function Only({ when, children }: { when: (ctx: ReturnType<typeof useCtx>) => boolean; children: ReactElement }) {
  const ctx = useCtx();
  return when(ctx) ? children : <Navigate to="/" replace />;
}
